import { httpResource } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImageOff } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { environment } from '../../../environments/environment';

/**
 * An `<img>` for images that need the bearer token (a plain `src` sends none).
 *
 * Fetches the image as a blob through HttpClient, so the auth interceptor applies,
 * and shows it from an object URL that is revoked on change and on destroy. Only
 * URLs under our API base are requested, and only `image/*` responses are shown.
 */
@Component({
  selector: 'app-authenticated-image',
  standalone: true,
  imports: [NgIcon, HlmButtonImports, HlmSkeletonImports, HlmSpinnerImports],
  providers: [provideIcons({ lucideImageOff })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div class="relative h-full w-full">
      @if (objectUrl(); as url) {
        <img
          [src]="url"
          [alt]="alt()"
          class="block h-full w-full transition-opacity"
          [class.object-contain]="fit() === 'contain'"
          [class.object-fill]="fit() === 'fill'"
          [class.opacity-40]="resource.isLoading()"
          (load)="onLoad($event)" />
      } @else if (resource.isLoading()) {
        <hlm-skeleton class="h-full w-full" />
      } @else if (resource.error() || rejected()) {
        <div
          class="text-muted-foreground flex h-full w-full flex-col items-center justify-center gap-1 text-xs">
          <ng-icon name="lucideImageOff" />
          <span>Bild konnte nicht geladen werden</span>
          @if (retry()) {
            <button hlmBtn variant="link" size="sm" type="button" (click)="resource.reload()">
              Erneut laden
            </button>
          }
        </div>
      }
      <!-- A picture the server still renders: after a moment, say so quietly. -->
      @if (resource.isLoading() && slow()) {
        <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
          <hlm-spinner size="sm" class="text-muted-foreground opacity-70" />
        </div>
      }
    </div>
  `,
})
export class AuthenticatedImageComponent {
  /** Absolute URL under `environment.apiUrl`; anything else is never requested. */
  readonly src = input.required<string>();
  readonly alt = input('');
  readonly retry = input(true);
  /**
   * `contain` keeps the picture's proportions inside the box; `fill` stretches it to
   * the box, for a picture that is the box (a page raster) and must leave no
   * sub-pixel gap at an edge.
   */
  readonly fit = input<'contain' | 'fill'>('contain');
  /** Natural size of the loaded image, for hosts that scale to it. */
  readonly loaded = output<{ width: number; height: number }>();

  readonly resource = httpResource.blob(() => {
    const url = this.src();
    return url.startsWith(environment.apiUrl) ? url : undefined;
  });

  protected readonly objectUrl = signal<string | null>(null);
  protected readonly rejected = signal(false);
  /** Loading for longer than a moment (the server is rendering the picture). */
  protected readonly slow = signal(false);
  private slowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const destroyRef = inject(DestroyRef);
    effect(() => {
      const blob = this.resource.value();
      const loading = this.resource.isLoading();
      untracked(() => {
        // A newer version on its way: keep the old picture, dimmed, instead of a blank.
        if (blob === undefined && loading && this.objectUrl()) return;
        this.swapObjectUrl(blob);
      });
    });
    effect(() => {
      const loading = this.resource.isLoading();
      untracked(() => this.watchSlow(loading));
    });
    destroyRef.onDestroy(() => {
      this.watchSlow(false);
      this.swapObjectUrl(undefined);
    });
  }

  private watchSlow(loading: boolean) {
    if (this.slowTimer) clearTimeout(this.slowTimer);
    this.slowTimer = null;
    if (loading) this.slowTimer = setTimeout(() => this.slow.set(true), 800);
    else this.slow.set(false);
  }

  protected onLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.loaded.emit({ width: img.naturalWidth, height: img.naturalHeight });
  }

  private swapObjectUrl(blob: Blob | undefined) {
    const previous = this.objectUrl();
    if (previous) URL.revokeObjectURL(previous);
    if (blob && blob.type.startsWith('image/')) {
      this.rejected.set(false);
      this.objectUrl.set(URL.createObjectURL(blob));
    } else {
      this.rejected.set(!!blob);
      this.objectUrl.set(null);
    }
  }
}
